"use client";
import { zodResolver } from "@hookform/resolvers/zod";
import { AlertTriangle, FileText, Upload, X , Loader2 } from "lucide-react";
import { parse } from "papaparse";
import {
  parseSpreadsheetInBrowser,
  detectHeadersFromRows,
  MAX_IMPORT_ROWS,
  type ParsedSpreadsheet,
  type SheetTab,
} from "@/app/_module/utils/parseSpreadsheetClient";
import type { FC } from "react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import type { FileWithPath } from "react-dropzone";
import Dropzone from "react-dropzone";
import { useForm } from "react-hook-form";
// Excel and CSV are BOTH parsed in the browser — no server reads a spreadsheet.
// See parseSpreadsheetClient.ts for why.

import { Button } from "@/app/_module/components/ui/button";
import { DialogFooter } from "@/app/_module/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/app/_module/components/ui/form";
import { cn } from "@/app/_module/lib/utils";

// mapFields import removed - mapping now handled in MapImport component
import { useGetLeadsImportList } from "../../hooks/leads-import-queries";
import type { TUploadLeadSchema } from "../../schema/upload-lead-schema";
import { uploadLeadSchema } from "../../schema/upload-lead-schema";
// customFieldsService import removed - now only used in MapImport

import type { TCurrentStep } from "./uploadLeadsDialog";
import { isDemoMode } from "@/app/_module/utils/demoMode";

// Type guard to check if File API is available
const isFileAvailable = typeof window !== "undefined" && typeof File !== "undefined";

// Import usage type passed from parent
interface ImportUsageData {
  currentUsage: number;
  limit: number;
  remaining: number;
  limitReached: boolean;
  accountType: string;
  isTrial: boolean;
  periodType: 'trial' | 'monthly';
  periodStart: string;
  periodEnd: string;
  resetMessage: string;
}

// eslint-disable-next-line no-restricted-syntax
interface IUploadCSV {
  setCurrentStep: React.Dispatch<React.SetStateAction<TCurrentStep>>;
  setCsvHeaders: React.Dispatch<React.SetStateAction<string[]>>;
  setMappings: React.Dispatch<React.SetStateAction<Record<string, string>>>; // correct
  setCsvData: React.Dispatch<React.SetStateAction<unknown[] | null>>; // correct
  setCsvFile: React.Dispatch<React.SetStateAction<FileWithPath | null>>; // correct
  setFileHasHeaders: React.Dispatch<React.SetStateAction<boolean>>;
  setDetectedTypes: React.Dispatch<React.SetStateAction<Record<string, string> | null>>;
  cancelImport: () => void;
  importUsage?: ImportUsageData | null;
}

const UploadCSV: FC<IUploadCSV> = function (prop) {
  const {
    setCurrentStep,
    setCsvHeaders,
    setMappings,
    setCsvData,
    setCsvFile,
    setFileHasHeaders,
    setDetectedTypes,
    cancelImport,
    importUsage,
  } = prop;

  // Shown while waiting out a busy importer. Only one spreadsheet parses at a
  // time per server instance (heap guard), so a concurrent import makes this
  // wait — and an unexplained 14-second spinner reads as broken.

  // Custom fields loading removed - mapping now handled in MapImport component

  const form = useForm<TUploadLeadSchema>({
    resolver: zodResolver(uploadLeadSchema),
    defaultValues: {
      file: [],
    },
    mode: "onChange",
  });

  const {
    control,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = form;

  const isFileError = errors.file;
  const watchedFiles = watch("file");

  // A workbook with leads on several tabs waits here until the user picks one.
  const [tabChoice, setTabChoice] = useState<{ book: File; tabs: SheetTab[] } | null>(null);
  const [readingTab, setReadingTab] = useState<string | null>(null);

  // Dumb-proofing: warn (don't block) if this exact filename was imported before,
  // so users don't accidentally re-upload the same list (the #1 source of "I
  // uploaded 700 but only got 590" confusion — most rows come back as duplicates).
  const { data: priorImportData } = useGetLeadsImportList({ refetchInterval: false });
  const priorImports: any[] = Array.isArray(priorImportData)
    ? priorImportData
    : ((priorImportData as any)?.data ?? []);
  const selectedFileName: string | undefined = (watchedFiles as any)?.[0]?.name;
  const duplicateImportWarning = useMemo(() => {
    if (!selectedFileName) return null;
    const norm = selectedFileName.trim().toLowerCase();
    const matches = priorImports.filter(
      (imp) => ((imp?.fileName || imp?.name || "") as string).trim().toLowerCase() === norm,
    );
    if (matches.length === 0) return null;
    const totalLeads = matches.reduce(
      (s, m) => s + (m?.resultCounts?.success || m?.processedRecords || 0),
      0,
    );
    const latest = matches.reduce((a, b) =>
      new Date(a?.createdAt || 0) > new Date(b?.createdAt || 0) ? a : b,
    );
    return { times: matches.length, totalLeads, date: latest?.createdAt as string | undefined };
  }, [selectedFileName, priorImports]);

  const handleCancel = () => {
    reset();
    cancelImport();
  };

  // Remove a specific file from the array
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const removeFile = (fileToRemove: any) => {
    if (!isFileAvailable) return;
    
    setValue(
      "file",
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      watchedFiles.filter((f: any) => f !== fileToRemove),
      { shouldValidate: true }
    );
    setTabChoice(null);
    setCurrentStep("upload");
  };

  const showSheet = useCallback(
    ({ headers, data, hasHeaders, detectedTypes }: ParsedSpreadsheet) => {
      setFileHasHeaders(hasHeaders);
      setDetectedTypes(detectedTypes);
      setCsvHeaders(headers);
      setCsvData(data);
      setCurrentStep("mapping");
    },
    [setFileHasHeaders, setDetectedTypes, setCsvHeaders, setCsvData, setCurrentStep],
  );

  // One tab per import. The tab goes into the file name, which names the lead list when
  // none is typed, so each tab lands as its own list ("Leads - Ali Beydoun").
  const pickTab = (name: string) => {
    if (!tabChoice) return;
    const { book } = tabChoice;
    form.clearErrors("file");
    setReadingTab(name);
    parseSpreadsheetInBrowser(book, name)
      .then((sheet) => {
        const base = book.name.replace(/\.[^.]+$/, "");
        setCsvFile(new File([book], `${base} - ${name}${book.name.slice(base.length)}`, { type: book.type }));
        showSheet(sheet);
      })
      .catch((err: unknown) =>
        form.setError("file", { message: err instanceof Error ? err.message : "Could not read that tab." }),
      )
      .finally(() => setReadingTab(null));
  };

// Excel parsing moved server-side for security. This helper posts file to API.

  const handleFileUpload = useCallback(
    (file: FileWithPath) => {
      if (!file) return;

      setCsvFile(file);
      setTabChoice(null);

      // Check file type and process accordingly
      const isCsv = file.type === 'text/csv' || file.name.endsWith('.csv');
      const isExcel =
        file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        file.type === 'application/vnd.ms-excel' ||
        file.name.endsWith('.xlsx') ||
        file.name.endsWith('.xls');

      // ONE cap for both formats now. Excel used to be held far tighter (10MB, then
      // 3MB, then 2MB) because a server parsed it inside a 512MB box and a big file
      // took the whole site down. It's parsed in the browser now, same as CSV, so the
      // tight cap has nothing left to protect — the limit is just "sane for an import".
      const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;
      if (file.size > MAX_UPLOAD_BYTES) {
        form.setError("file", {
          message: `File too large — max 25MB / ${MAX_IMPORT_ROWS.toLocaleString()} rows. Split it into smaller batches.`,
        });
        setCsvFile(null);
        return;
      }

      if (isCsv) {
        // Process CSV file - CSV uses papaparse which always assumes first row is headers
        // For CSV files, we assume they have headers (papaparse default behavior)
        setFileHasHeaders(true);
        setDetectedTypes(null);
        parse(file, {
          header: true,
          skipEmptyLines: true,
          // Strip BOM from first header and trim whitespace
          transformHeader: (header, index) => {
            let cleaned = header.trim();
            // Strip UTF-8 BOM from first header if present
            if (index === 0) {
              cleaned = cleaned.replace(/^\uFEFF/, '');
            }
            return cleaned;
          },
          complete: (results) => {
            const headers = results.meta.fields || [];
            const rawData = results.data || [];

            // If the CSV arrived with AUTO-GENERATED generic headers ("Column 1",
            // "Column 8 (City)") — e.g. a re-export of a headerless import — papaparse
            // can't map it and would follow the bad labels (DOB unmapped, names in
            // city). Re-parse through the detection route so content-based type
            // detection runs, exactly like Excel. Normal CSVs (real headers) are
            // untouched.
            const genericCount = headers.filter((h) => /^column\s*\d+/i.test(String(h).trim())).length;
            if (headers.length > 0 && genericCount >= Math.max(1, Math.ceil(headers.length * 0.5))) {
              // Re-detect on the rows we ALREADY have. This used to re-upload the file to
              // a server route to be parsed a second time; the rows are right here, so the
              // detection runs on them directly — no upload, no second parse, no server.
              const asArrays = rawData.map((row) =>
                headers.map((h) => (row as Record<string, unknown>)[h] as string | number | boolean | null),
              );
              const detected = detectHeadersFromRows(asArrays.slice(0, 20), headers.length);
              const remapped = asArrays.map((row) => {
                const rec: Record<string, unknown> = {};
                detected.headers.forEach((h, i) => { rec[h] = row[i] ?? ""; });
                return rec;
              });
              setFileHasHeaders(false);
              setDetectedTypes(detected.detectedTypes);
              setCsvHeaders(detected.headers);
              setCsvData(remapped);
              setCurrentStep("mapping");
              return;
            }

            // FollowUpBoss-style export detection: identify key columns vs notes columns
            // Key columns indicate a "real" lead row, notes columns contain additional info
            const keyColumnNames: string[] = [];
            const notesColumnNames: string[] = [];

            headers.forEach((header: string) => {
              const h = header.toLowerCase().replace(/[_\s]/g, ''); // Normalize: remove spaces and underscores
              // Key columns that indicate a real lead
              if (h === 'name' || h === 'firstname' || h === 'lastname' ||
                  h === 'fname' || h === 'lname' || h === 'fullname' ||
                  h === 'dateadded' || h.startsWith('phone') || h.startsWith('email') ||
                  h === 'stage' || h === 'leadsource' || h === 'assignedto' ||
                  h === 'address' || h === 'city' || h === 'state' || h === 'zip' || h === 'zipcode') {
                keyColumnNames.push(header);
              }
              // Notes columns that may have continuation data
              if (h === 'notes' || h === 'note' || h === 'message' ||
                  h === 'description' || h === 'background' || h === 'comment' || h === 'comments') {
                notesColumnNames.push(header);
              }
            });

            // Process rows: merge notes continuation rows into real leads
            // Only apply FollowUpBoss-style continuation filtering when the CSV has
            // notes columns AND recognized key columns — otherwise treat all rows as leads.
            const isFollowUpBossStyle = notesColumnNames.length > 0 && keyColumnNames.length > 0;
            const processedData: unknown[] = [];

            rawData.forEach((row: unknown) => {
              const rowObj = row as Record<string, unknown>;

              // Check if this row has any content at all
              let hasAnyContent = false;
              for (const key of Object.keys(rowObj)) {
                const val = rowObj[key];
                if (val !== null && val !== undefined && String(val).trim() !== '') {
                  hasAnyContent = true;
                  break;
                }
              }

              if (!hasAnyContent) {
                // Skip completely empty rows
                return;
              }

              if (!isFollowUpBossStyle) {
                // Standard CSV — every non-empty row is a lead
                processedData.push(rowObj);
                return;
              }

              // FollowUpBoss-style: detect notes continuation rows
              // Count how many key columns have real content
              let keyColumnsWithContent = 0;
              for (const colName of keyColumnNames) {
                const val = rowObj[colName];
                if (val !== null && val !== undefined && String(val).trim() !== '') {
                  keyColumnsWithContent++;
                }
              }

              // A row is considered a real lead if it has at least 3 key columns filled
              const isRealLead = keyColumnsWithContent >= 3;

              if (isRealLead) {
                // This is a real lead row
                processedData.push(rowObj);
              } else if (processedData.length > 0) {
                // This is a notes continuation row - merge notes into previous lead
                const prevLead = processedData[processedData.length - 1] as Record<string, unknown>;

                for (const notesCol of notesColumnNames) {
                  const notesVal = rowObj[notesCol];
                  if (notesVal !== null && notesVal !== undefined && String(notesVal).trim() !== '') {
                    const existingNotes = prevLead[notesCol] ? String(prevLead[notesCol]) : '';
                    const newNotes = String(notesVal).trim();
                    if (existingNotes) {
                      prevLead[notesCol] = existingNotes + '\n---\n' + newNotes;
                    } else {
                      prevLead[notesCol] = newNotes;
                    }
                  }
                }
              } else {
                // First row doesn't meet key column threshold but has content
                // Still add it as a lead (could be sparse data)
                processedData.push(rowObj);
              }
            });

            // Reject imports over the shared row cap (same as Excel)
            if (processedData.length > MAX_IMPORT_ROWS) {
              form.setError("file", {
                message: `File contains ${processedData.length.toLocaleString()} leads which exceeds the maximum of ${MAX_IMPORT_ROWS.toLocaleString()}. Please split your file into smaller batches.`,
              });
              setCsvFile(null);
              return;
            }

            setCsvHeaders(headers);
            setCsvData(processedData);
            // Don't set mappings here - let MapImport handle it consistently
            setCurrentStep("mapping");
          },
        });
      } else if (isExcel) {
        // Parsed HERE, always. There is deliberately NO server fallback: the only server
        // that ever parsed spreadsheets was the 512MB frontend box, and one workbook
        // expanding past its heap took the whole site down twice in 90 seconds
        // (2026-09-14). A file this browser cannot read is an error the user sees, not
        // work we hand to a machine everyone else depends on.
        parseSpreadsheetInBrowser(file)
          .then((result) => {
            // Leads on several tabs: ask which (picker below), never guess.
            if ("tabs" in result) setTabChoice({ book: file, tabs: result.tabs });
            else showSheet(result);
          })
          .catch((err) => {
            form.setError("file", { message: err?.message || "Could not read that spreadsheet." });
            setCsvFile(null);
          });
        // setCurrentStep happens inside .then(), so fileHasHeaders and detectedTypes are
        // set before MapImport renders.
        return;
      }

      setCurrentStep("mapping");
    },
    [setCsvFile, setCsvData, setCsvHeaders, setCurrentStep, setMappings, setFileHasHeaders, setDetectedTypes, showSheet]
  );

  /**
   * DEMO SANDBOX — load one of the bundled sample CSVs.
   *
   * A visitor has no lead file on their machine, so the import flow is a dead end for
   * them without this. Fetches the file the app already ships and pushes it through the
   * SAME handler a dropped file uses, so mapping, household detection and the row-count
   * preview all behave identically to a real import.
   */
  const useSampleCsv = useCallback(
    async (fileName: string) => {
      try {
        const res = await fetch(`/demo/${fileName}`);
        if (!res.ok) throw new Error(`sample not found (${res.status})`);
        const blob = await res.blob();
        const file = new File([blob], fileName, { type: "text/csv" }) as FileWithPath;
        handleFileUpload(file);
        setValue("file", [file] as never, { shouldValidate: true });
      } catch (err) {
        // Non-fatal: the visitor can still drag their own file in.
        console.warn("[demo] sample CSV load failed", err);
      }
    },
    [handleFileUpload, setValue],
  );


  return (
    <Form {...form}>
      <form className="space-y-4">
        <div className="space-y-4">
          <div className="space-y-2">
            <FormField
              control={control}
              name="file"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Dropzone
                      multiple={false}
                      maxFiles={1}
                      accept={{
                        "text/csv": [".csv"],
                        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
                        "application/vnd.ms-excel": [".xls"],
                      }}
                      onDrop={(acceptedFiles) => {
                        if (acceptedFiles && acceptedFiles.length) {
                          if (acceptedFiles[0]) {
                            handleFileUpload(acceptedFiles[0]);
                          }
                          setValue("file", acceptedFiles.slice(0, 1), {
                            shouldValidate: true,
                          });
                        }
                      }}
                    >
                      {({
                        getRootProps,
                        getInputProps,
                        isDragActive,
                        isDragReject,
                        fileRejections,
                      }) => (
                        <React.Fragment>
                          <div
                            {...getRootProps({
                              // No file picker in the sandbox — the sample buttons below
                              // are the only way in, so a click shouldn't open a dialog
                              // the visitor has nothing to put in.
                              onClick: isDemoMode()
                                ? (e: React.MouseEvent) => e.stopPropagation()
                                : undefined,
                              className: cn(
                                "relative border-2 border-dashed rounded-lg p-6 transition-colors",
                                isDemoMode() ? "cursor-default" : "cursor-pointer",
                                {
                                  "border-primary bg-primary/5": isDragActive,
                                  "border-red-500 bg-red-50":
                                    isDragReject || isFileError,
                                  "border-muted-foreground/25 hover:border-muted-foreground/50":
                                    !isDragActive &&
                                    !isDragReject &&
                                    !isFileError,
                                }
                              ),
                            })}
                          >
                            {!isDemoMode() && <input {...getInputProps()} />}
                            <div className="text-center">
                              <Upload className="mx-auto h-8 w-8 text-muted-foreground mb-2" />
                              {isDemoMode() ? (
                                <>
                                  <p className="text-sm font-medium text-muted-foreground mb-1">
                                    Pick a sample lead list to import
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    The demo imports a ready-made list — on a real account you drop
                                    in your own CSV or Excel file here.
                                  </p>
                                </>
                              ) : (
                                <>
                                  <p className="text-sm font-medium text-muted-foreground mb-1">
                                    Drop your file here, or click to browse
                                    <span className="font-semibold text-foreground"> (1 file only)</span>
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    CSV or Excel up to 25MB · max {MAX_IMPORT_ROWS.toLocaleString()} rows per file · 1 file at a time
                                  </p>
                                </>
                              )}
                            </div>
                          </div>
                          {/* Show accepted files */}
                          {!!field.value.length && !!isFileAvailable && (
                            <div className="space-y-2 mt-2">
                              {
                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                field.value.map((file: any) => (
                                <div
                                  key={file.name}
                                  className="flex items-center justify-between p-3 bg-muted rounded-lg"
                                >
                                  <div className="flex items-center gap-2">
                                    <FileText className="w-4 h-4 text-muted-foreground" />
                                    <div>
                                      <p className="text-sm font-medium">
                                        {file.name}
                                      </p>
                                      <p className="text-xs text-muted-foreground">
                                        {(file.size / 1024).toFixed(1)} KB
                                      </p>
                                    </div>
                                  </div>
                                  <Button
                                    className="h-8 w-8 p-0"
                                    onClick={() => removeFile(file)}
                                    size="sm"
                                    type="button"
                                    variant="ghost"
                                  >
                                    <X className="w-4 h-4" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}
                          {tabChoice ? (
                            <div className="mt-2 rounded-lg border p-3">
                              <p className="text-sm font-medium">
                                This workbook has leads on {tabChoice.tabs.length} tabs. Which one do you want to import?
                              </p>
                              <p className="mt-0.5 mb-2 text-xs text-muted-foreground">
                                Tabs import one at a time, each as its own lead list. To import another tab,
                                upload this file again after this one.
                              </p>
                              <div className="space-y-1.5">
                                {tabChoice.tabs.map((t) => (
                                  <Button
                                    key={t.name}
                                    className="w-full justify-between gap-3"
                                    disabled={readingTab !== null}
                                    onClick={() => pickTab(t.name)}
                                    type="button"
                                    variant="outline"
                                  >
                                    <span className="min-w-0 truncate">{t.name}</span>
                                    <span className="shrink-0 text-xs text-muted-foreground">
                                      {readingTab === t.name
                                        ? "Reading…"
                                        : `${t.rows.toLocaleString()} ${t.rows === 1 ? "row" : "rows"}`}
                                    </span>
                                  </Button>
                                ))}
                              </div>
                            </div>
                          ) : null}
                          {/* Dumb-proof: already-imported-this-file warning (non-blocking) */}
                          {duplicateImportWarning && (
                            <div className="mt-2 flex items-start gap-2 p-3 rounded-lg border border-red-200 bg-red-50 text-red-800">
                              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                              <p className="text-xs leading-relaxed">
                                <span className="font-medium">
                                  You already imported a file named &quot;{selectedFileName}&quot;
                                  {duplicateImportWarning.times > 1 ? ` ${duplicateImportWarning.times} times` : ""}
                                </span>
                                {duplicateImportWarning.date
                                  ? ` (last on ${new Date(duplicateImportWarning.date).toLocaleDateString()}, ${duplicateImportWarning.totalLeads.toLocaleString()} leads added).`
                                  : "."}{" "}
                                Re-importing is fine — rows that already exist are skipped automatically as duplicates — but you may not get many new leads. This upload is kept as its own separate list.
                              </p>
                            </div>
                          )}
                          {/* Sandbox: nobody has a lead file on a demo machine. */}
                          {isDemoMode() && (
                            <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50/60 p-3">
                              <p className="text-xs font-medium text-blue-900 mb-2">
                                Sample lead lists:
                              </p>
                              <div className="flex flex-wrap gap-2">
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    void useSampleCsv("demo-leads.csv");
                                  }}
                                  className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 active:scale-95"
                                >
                                  40 leads (with households)
                                </button>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    void useSampleCsv("demo-leads-messy.csv");
                                  }}
                                  className="rounded-md border border-blue-300 bg-white px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-50 active:scale-95"
                                >
                                  25 leads (messy headers)
                                </button>
                              </div>
                            </div>
                          )}

                          {/* Show error for rejected files */}
                          {fileRejections.length > 0 && (
                            <FormMessage className="text-xs text-red-500">
                              Only CSV, XLSX, and XLS files are accepted.
                            </FormMessage>
                          )}
                        </React.Fragment>
                      )}
                    </Dropzone>
                  </FormControl>
                  {!!isFileError && <FormMessage className="text-xs" />}
                </FormItem>
              )}
            />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={handleCancel} type="button" variant="outline">
            Cancel
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
};

export default UploadCSV;
